import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Field } from '@cordata/buffet';
import { Row, Col } from 'reactstrap';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';

import useCit from '~/utils/cit/hook';

import MedicalEventCitFields from '~/components/MedicalEvent/Form/CIT';

const Fieldset = ({ children, disabled }) => (
  <fieldset className="form-section" disabled={disabled}>
    {children}
  </fieldset>
);

Fieldset.propTypes = {
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
};

Fieldset.defaultProps = {
  disabled: false,
};

const PublicFields = () => {
  const {
    values, setFieldValue, setStatus,
  } = useFormikContext();

  const {
    authStatus,
    formData,
    actions: {
      setAuthStatus,
      identify,
      getContext,
    },
  } = useCit();

  const toastId = 'identify-this';
  const invalidMessage = 'Agency Identifier or Officer Name is invalid';

  useEffect(() => {
    if (values.type === 'Personal Residence') {
      setFieldValue('locationAddr1', values.addr1 || '');
      setFieldValue('locationAddr2', values.addr2 || '');
      setFieldValue('locationCity', values.city);
      setFieldValue('locationStateUid', values.stateUid);
      setFieldValue('locationCounty', values.county);
      setFieldValue('locationPostCode', values.postCode);
    }
  }, [values.type]);

  const verify = useCallback(debounce((v) => {
    const theBad = () => {
      toast.update(toastId, {
        render: invalidMessage,
        type: toast.TYPE.ERROR,
        autoClose: 2500,
      });

      setAuthStatus('INVALID');
    };

    identify(v)
      .then(getContext)
      .then(() => {
        toast.update(toastId, { render: 'Identity Success!', type: toast.TYPE.SUCCESS, autoClose: 2500 });

        setFieldValue('officer', v.identityName);
        setFieldValue('dateTime', new Date());
        setStatus({ success: false, payload: {} });
        setAuthStatus('VALID');
      })
      .catch(theBad);
  }, 750), []);

  useEffect(() => {
    if (values.code && values.officerName) {
      toast('Checking for identity ...', { toastId, autoClose: false });
      setAuthStatus('VERIFYING');

      verify({
        code: values.code,
        identityName: values.officerName,
      });
    }
  }, [values.code, values.officerName]);

  return (
    <React.Fragment>
      <Fieldset>
        <Row form>
          <Col md>
            <Field
              groupClassName="mb-1"
              name="code"
              label="Agency Identifier"
              disabled={authStatus === 'CHECKING'}
            />
          </Col>

          <Col md>
            <Field
              groupClassName="mb-1"
              name="officerName"
              disabled={authStatus === 'CHECKING'}
            />
          </Col>
        </Row>
      </Fieldset>

      { authStatus === 'VALID' && (
        <MedicalEventCitFields formData={formData} />
      )}
    </React.Fragment>
  );
};

export default PublicFields;
